import { formatRfc3339, subtractUtcMonths } from '@brightlocal/shared';

import { REVIEW_PERIOD } from '../consts';
import type { ReviewPeriod } from '../types';

/** Units a time period counts back in. */
const PERIOD_UNIT = {
  DAYS: 'days',
  MONTHS: 'months'
} as const;

/** A unit a time period counts back in. */
type PeriodUnit = (typeof PERIOD_UNIT)[keyof typeof PERIOD_UNIT];

/** How far back a period reaches; `null` for a period with no start. */
type PeriodStart = { unit: PeriodUnit; amount: number } | null;

const PERIOD_START: Record<ReviewPeriod, PeriodStart> = {
  [REVIEW_PERIOD.LAST_30_DAYS]: { unit: PERIOD_UNIT.DAYS, amount: 30 },
  [REVIEW_PERIOD.LAST_3_MONTHS]: { unit: PERIOD_UNIT.MONTHS, amount: 3 },
  [REVIEW_PERIOD.LAST_6_MONTHS]: { unit: PERIOD_UNIT.MONTHS, amount: 6 },
  [REVIEW_PERIOD.LAST_12_MONTHS]: { unit: PERIOD_UNIT.MONTHS, amount: 12 },
  [REVIEW_PERIOD.ALL_TIME]: null
};

const startOfUtcDayDaysBack = ({ now, days }: { now: Date; days: number }) =>
  Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - days);

const startOfUtcDayMonthsBack = ({ now, months }: { now: Date; months: number }) =>
  subtractUtcMonths({ date: new Date(startOfUtcDayDaysBack({ now, days: 0 })), months });

/** Inputs of {@link buildPeriodRange}. */
interface BuildPeriodRangeParams {
  /** The picked time period. */
  period: ReviewPeriod;
  /** The moment the range is built for. */
  now: Date;
}

/**
 * Turns a time period into the `from` of a request's date range. `from` starts at the beginning of a
 * UTC day, so every render within a day builds the same query key; `to` is left out, so the API runs
 * up to the most recent review. All time leaves out `from` too.
 */
export const buildPeriodRange = ({ period, now }: BuildPeriodRangeParams) => {
  const start = PERIOD_START[period];

  if (start === null) {
    return {};
  }

  const timestamp =
    start.unit === PERIOD_UNIT.DAYS
      ? startOfUtcDayDaysBack({ now, days: start.amount })
      : startOfUtcDayMonthsBack({ now, months: start.amount });

  return { from: formatRfc3339(new Date(timestamp)) };
};
